package com.wintermar.inspection

import app.cash.sqldelight.Query
import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Double
import kotlin.Long
import kotlin.String
import kotlin.collections.Collection
import migrations.AnswerLocationTable

public class AnswerLocationTableQueries(
  driver: SqlDriver,
) : TransacterImpl(driver) {
  public fun <T : Any> getAnswerLocationByAnswerId(answerId: String, mapper: (
    id: String,
    answerId: String,
    name: String?,
    latitude: Double?,
    longitude: Double?,
    street: String?,
    isoCountryCode: String?,
    country: String?,
    postalCode: String?,
    administrativeArea: String?,
    subAdministrativeArea: String?,
    locality: String?,
    subLocality: String?,
    thoroughfare: String?,
    subThoroughfare: String?,
  ) -> T): Query<T> = GetAnswerLocationByAnswerIdQuery(answerId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getDouble(3),
      cursor.getDouble(4),
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7),
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14)
    )
  }

  public fun getAnswerLocationByAnswerId(answerId: String): Query<AnswerLocationTable> = getAnswerLocationByAnswerId(answerId, ::AnswerLocationTable)

  /**
   * @return The number of rows updated.
   */
  public fun insertAnswerLocation(
    id: String,
    answerId: String,
    name: String?,
    latitude: Double?,
    longitude: Double?,
    street: String?,
    isoCountryCode: String?,
    country: String?,
    postalCode: String?,
    administrativeArea: String?,
    subAdministrativeArea: String?,
    locality: String?,
    subLocality: String?,
    thoroughfare: String?,
    subThoroughfare: String?,
  ): QueryResult<Long> {
    val result = driver.execute(-1_838_888_943, """
        |INSERT INTO AnswerLocationTable(id, answerId, name, latitude, longitude, street, isoCountryCode, country, postalCode, administrativeArea, subAdministrativeArea, locality, subLocality, thoroughfare, subThoroughfare)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        |ON CONFLICT(id) DO UPDATE SET
        |   answerId=excluded.answerId,
        |   name=excluded.name,
        |   latitude=excluded.latitude,
        |   longitude=excluded.longitude,
        |   street=excluded.street,
        |   isoCountryCode=excluded.isoCountryCode,
        |   country=excluded.country,
        |   postalCode=excluded.postalCode,
        |   administrativeArea=excluded.administrativeArea,
        |   subAdministrativeArea=excluded.subAdministrativeArea,
        |   locality=excluded.locality,
        |   subLocality=excluded.subLocality,
        |   thoroughfare=excluded.thoroughfare,
        |   subThoroughfare=excluded.subThoroughfare
        """.trimMargin(), 15) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindString(parameterIndex++, answerId)
          bindString(parameterIndex++, name)
          bindDouble(parameterIndex++, latitude)
          bindDouble(parameterIndex++, longitude)
          bindString(parameterIndex++, street)
          bindString(parameterIndex++, isoCountryCode)
          bindString(parameterIndex++, country)
          bindString(parameterIndex++, postalCode)
          bindString(parameterIndex++, administrativeArea)
          bindString(parameterIndex++, subAdministrativeArea)
          bindString(parameterIndex++, locality)
          bindString(parameterIndex++, subLocality)
          bindString(parameterIndex++, thoroughfare)
          bindString(parameterIndex++, subThoroughfare)
        }
    notifyQueries(-1_838_888_943) { emit ->
      emit("AnswerLocationTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun insertReplaceAnswerLocation(
    id: String,
    answerId: String,
    name: String?,
    latitude: Double?,
    longitude: Double?,
    street: String?,
    isoCountryCode: String?,
    country: String?,
    postalCode: String?,
    administrativeArea: String?,
    subAdministrativeArea: String?,
    locality: String?,
    subLocality: String?,
    thoroughfare: String?,
    subThoroughfare: String?,
  ): QueryResult<Long> {
    val result = driver.execute(1_502_547_241, """
        |INSERT OR REPLACE INTO AnswerLocationTable(id, answerId, name, latitude, longitude, street, isoCountryCode, country, postalCode, administrativeArea, subAdministrativeArea, locality, subLocality, thoroughfare, subThoroughfare)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 15) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindString(parameterIndex++, answerId)
          bindString(parameterIndex++, name)
          bindDouble(parameterIndex++, latitude)
          bindDouble(parameterIndex++, longitude)
          bindString(parameterIndex++, street)
          bindString(parameterIndex++, isoCountryCode)
          bindString(parameterIndex++, country)
          bindString(parameterIndex++, postalCode)
          bindString(parameterIndex++, administrativeArea)
          bindString(parameterIndex++, subAdministrativeArea)
          bindString(parameterIndex++, locality)
          bindString(parameterIndex++, subLocality)
          bindString(parameterIndex++, thoroughfare)
          bindString(parameterIndex++, subThoroughfare)
        }
    notifyQueries(1_502_547_241) { emit ->
      emit("AnswerLocationTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteAnswerLocationByAnswerId(answerId: String): QueryResult<Long> {
    val result = driver.execute(-1_584_493_869, """DELETE FROM AnswerLocationTable WHERE answerId = ?""", 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, answerId)
        }
    notifyQueries(-1_584_493_869) { emit ->
      emit("AnswerLocationTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteAnswerLocationByAnswerIds(answerId: Collection<String>): QueryResult<Long> {
    val answerIdIndexes = createArguments(count = answerId.size)
    val result = driver.execute(null, """DELETE FROM AnswerLocationTable WHERE answerId IN $answerIdIndexes""", answerId.size) {
          var parameterIndex = 0
          answerId.forEach { answerId_ ->
            bindString(parameterIndex++, answerId_)
          }
        }
    notifyQueries(-1_874_669_568) { emit ->
      emit("AnswerLocationTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteAllAnswerLocation(): QueryResult<Long> {
    val result = driver.execute(-1_426_775_644, """DELETE FROM AnswerLocationTable""", 0)
    notifyQueries(-1_426_775_644) { emit ->
      emit("AnswerLocationTable")
    }
    return result
  }

  private inner class GetAnswerLocationByAnswerIdQuery<out T : Any>(
    public val answerId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("AnswerLocationTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("AnswerLocationTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(747_830_100, """SELECT AnswerLocationTable.id, AnswerLocationTable.answerId, AnswerLocationTable.name, AnswerLocationTable.latitude, AnswerLocationTable.longitude, AnswerLocationTable.street, AnswerLocationTable.isoCountryCode, AnswerLocationTable.country, AnswerLocationTable.postalCode, AnswerLocationTable.administrativeArea, AnswerLocationTable.subAdministrativeArea, AnswerLocationTable.locality, AnswerLocationTable.subLocality, AnswerLocationTable.thoroughfare, AnswerLocationTable.subThoroughfare FROM AnswerLocationTable WHERE answerId = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, answerId)
    }

    override fun toString(): String = "AnswerLocationTable.sq:getAnswerLocationByAnswerId"
  }
}
